/**
 * Tachograph data access: TanStack Query hooks over the typed client. Other modules may use
 * these hooks (the web side of tacho's service functions), never tacho's pages.
 */
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, unwrap } from '@/lib/api/client';
import type { ActionOut, CardPatch, RegisterCard, RequestDownload, SetSchedule, TargetKind } from './types';

export const tachoKeys = {
  all: ['tacho'] as const,
  overview: (p: object) => ['tacho', 'overview', p] as const,
  live: (p: object) => ['tacho', 'live', p] as const,
  hours: (driverId: string) => ['tacho', 'hours', driverId] as const,
  activities: (p: object) => ['tacho', 'activities', p] as const,
  infringements: (p: object) => ['tacho', 'infringements', p] as const,
  stats: (p: object) => ['tacho', 'stats', p] as const,
  infringement: (id: string) => ['tacho', 'infringement', id] as const,
  files: (p: object) => ['tacho', 'files', p] as const,
  file: (id: string) => ['tacho', 'file', id] as const,
  parsed: (id: string) => ['tacho', 'parsed', id] as const,
  schedules: (p: object) => ['tacho', 'schedules', p] as const,
  attention: (p: object) => ['tacho', 'attention', p] as const,
  downloads: (p: object) => ['tacho', 'downloads', p] as const,
  cards: ['tacho', 'cards'] as const,
};

const iso = (d: Date | undefined) => d?.toISOString();

export function useTachoOverview(project: string[] | undefined, enabled = true) {
  return useQuery({
    queryKey: tachoKeys.overview({ project }),
    queryFn: () => unwrap(api.GET('/api/tacho/overview', { params: { query: { project } } })),
    enabled,
  });
}

/** Drivers with live tachograph data in the last 24 h and their hours left (refreshes every minute). */
export function useLive(project: string[] | undefined, enabled = true) {
  return useQuery({
    queryKey: tachoKeys.live({ project }),
    queryFn: () => unwrap(api.GET('/api/tacho/live', { params: { query: { project, limit: 200 } } })),
    refetchInterval: 60_000,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useDriverHours(driverId: string, enabled = true) {
  return useQuery({
    queryKey: tachoKeys.hours(driverId),
    queryFn: () => unwrap(api.GET('/api/tacho/drivers/{driver_id}/hours', { params: { path: { driver_id: driverId } } })),
    refetchInterval: 60_000,
    enabled: enabled && !!driverId,
  });
}

export interface ActivityQuery {
  driver?: string;
  vehicle?: string;
  since: Date;
  until: Date;
}

export function useActivities(q: ActivityQuery, enabled = true) {
  const key = { ...q, since: iso(q.since), until: iso(q.until) };
  return useQuery({
    queryKey: tachoKeys.activities(key),
    queryFn: () =>
      unwrap(
        api.GET('/api/tacho/activities', {
          params: { query: { driver: q.driver, vehicle: q.vehicle, since: key.since, until: key.until, limit: 500 } },
        }),
      ),
    enabled: enabled && !!(q.driver || q.vehicle),
  });
}

export interface InfringementQuery {
  project?: string[];
  driver?: string;
  vehicle?: string;
  rule?: string;
  severity?: 'MI' | 'SI' | 'VSI' | 'MSI';
  status?: 'open' | 'acknowledged' | 'withdrawn';
  since?: Date;
  until?: Date;
}

export function useInfringements(q: InfringementQuery, enabled = true, limit = 100) {
  const query = { ...q, since: iso(q.since), until: iso(q.until) };
  return useInfiniteQuery({
    queryKey: tachoKeys.infringements({ ...query, limit }),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/tacho/infringements', { params: { query: { ...query, cursor: pageParam, limit } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useInfringementStats(q: { project?: string[]; driver?: string; since?: Date; until?: Date }, enabled = true) {
  const query = { ...q, since: iso(q.since), until: iso(q.until) };
  return useQuery({
    queryKey: tachoKeys.stats(query),
    queryFn: () => unwrap(api.GET('/api/tacho/infringements/stats', { params: { query } })),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useInfringement(id: string | undefined) {
  return useQuery({
    queryKey: tachoKeys.infringement(id ?? ''),
    queryFn: () => unwrap(api.GET('/api/tacho/infringements/{infringement_id}', { params: { path: { infringement_id: id ?? '' } } })),
    enabled: !!id,
  });
}

/** Every write returns the action result (message, data, links). */
function useAction<V>(fn: (v: V) => Promise<ActionOut>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: tachoKeys.all }) });
}

export function useAcknowledge() {
  return useAction((v: { id: string; note: string; version: number }) =>
    unwrap(
      api.POST('/api/tacho/infringements/{infringement_id}/acknowledge', {
        params: { path: { infringement_id: v.id } },
        body: { note: v.note, version: v.version },
      }),
    ),
  );
}

export function useEvaluate() {
  return useAction((v: { driverId: string; start: string; end: string }) =>
    unwrap(api.POST('/api/tacho/drivers/{driver_id}/evaluate', { params: { path: { driver_id: v.driverId } }, body: { start: v.start, end: v.end } })),
  );
}

export interface FileQuery {
  project?: string[];
  kind?: TargetKind;
  driver?: string;
  vehicle?: string;
  archived?: boolean;
  since?: Date;
  until?: Date;
}

export function useFiles(q: FileQuery, enabled = true, limit = 100) {
  const query = { ...q, since: iso(q.since), until: iso(q.until) };
  return useInfiniteQuery({
    queryKey: tachoKeys.files({ ...query, limit }),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/tacho/files', { params: { query: { ...query, cursor: pageParam, limit } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useFile(id: string) {
  return useQuery({
    queryKey: tachoKeys.file(id),
    queryFn: () => unwrap(api.GET('/api/tacho/files/{file_id}', { params: { path: { file_id: id } } })),
  });
}

export function useParsed(id: string, enabled: boolean) {
  return useQuery({
    queryKey: tachoKeys.parsed(id),
    queryFn: () => unwrap(api.GET('/api/tacho/files/{file_id}/parsed', { params: { path: { file_id: id } } })),
    enabled,
    retry: (n, e) => !(e instanceof ApiError && [404, 410].includes(e.status)) && n < 2,
  });
}

/** Save a response body as a file (the browser's download). */
export function saveBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** The raw file exactly as received (the legal record). Access is logged by the server. */
export async function downloadRawFile(id: string): Promise<void> {
  const { data, error, response } = await api.GET('/api/tacho/files/{file_id}/raw', { params: { path: { file_id: id } }, parseAs: 'blob' });
  if (error !== undefined || !response.ok || !data) {
    const detail = error && typeof error === 'object' && 'detail' in error ? (error as { detail: unknown }).detail : undefined;
    throw new ApiError(response.status, detail);
  }
  const name = /filename="([^"]+)"/.exec(response.headers.get('Content-Disposition') ?? '')?.[1] ?? `tacho-${id.slice(-8)}.ddd`;
  saveBlob(data as Blob, name);
}

export interface UploadInput {
  file: File;
  kind?: TargetKind;
  project_id?: string;
  driver_id?: string;
  vehicle_id?: string;
}

export function useUpload() {
  return useAction(async (v: UploadInput) =>
    unwrap(
      api.POST('/api/tacho/files', {
        body: { file: v.file.name, kind: v.kind, project_id: v.project_id, driver_id: v.driver_id, vehicle_id: v.vehicle_id },
        bodySerializer: () => {
          const fd = new FormData();
          fd.append('file', v.file);
          for (const k of ['kind', 'project_id', 'driver_id', 'vehicle_id'] as const) if (v[k]) fd.append(k, v[k]);
          return fd;
        },
      }),
    ),
  );
}

export function useSchedules(q: { project?: string[]; kind?: TargetKind; enabled?: boolean }, enabled = true) {
  return useInfiniteQuery({
    queryKey: tachoKeys.schedules(q),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/tacho/schedules', { params: { query: { ...q, cursor: pageParam, limit: 200 } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useAttention(project: string[] | undefined, enabled = true) {
  return useQuery({
    queryKey: tachoKeys.attention({ project }),
    queryFn: () => unwrap(api.GET('/api/tacho/attention', { params: { query: { project } } })),
    enabled,
  });
}

export function useSetSchedule() {
  return useAction((body: SetSchedule) => unwrap(api.POST('/api/tacho/schedules', { body })));
}

export interface DownloadQuery {
  project?: string[];
  status?: 'scheduled' | 'requested' | 'in_progress' | 'completed' | 'failed' | 'timed_out';
  vehicle?: string;
  driver?: string;
  since?: Date;
}

/** Download jobs; re-read every 30 s while any loaded job is still in flight. */
export function useDownloads(q: DownloadQuery, enabled = true, limit = 100) {
  const query = { ...q, since: iso(q.since) };
  return useInfiniteQuery({
    queryKey: tachoKeys.downloads({ ...query, limit }),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/tacho/downloads', { params: { query: { ...query, cursor: pageParam, limit } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
    refetchInterval: (query) =>
      query.state.data?.pages.some((p) => p.items.some((d) => ['scheduled', 'requested', 'in_progress'].includes(d.status))) ? 30_000 : false,
    enabled,
  });
}

export function useRequestDownload() {
  return useAction((body: RequestDownload) => unwrap(api.POST('/api/tacho/downloads', { body })));
}

export function useCards(enabled = true) {
  return useQuery({ queryKey: tachoKeys.cards, queryFn: () => unwrap(api.GET('/api/tacho/cards')), enabled });
}

export function useRegisterCard() {
  return useAction((body: RegisterCard) => unwrap(api.POST('/api/tacho/cards', { body })));
}

export function useUpdateCard(id: string) {
  return useAction((body: CardPatch) => unwrap(api.PATCH('/api/tacho/cards/{card_id}', { params: { path: { card_id: id } }, body })));
}
